import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Avatar,
  Checkbox, Typography, Box, Chip, Tabs, Tab, InputAdornment, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { useApi } from '../../contexts/ApiContext';
import { useChat } from '../../contexts/ChatContext';

const accent = '#7C3AED';

const NewConversationDialog = ({ open, onClose, onCreated, canCreateGroup = true, canMessageAgents = true }) => {
  const { im } = useApi();
  const { myId } = useChat();
  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      im.getAgents().then(res => {
        if (res.success) {
          setAgents(res.data.filter(a => a.id !== myId));
        }
      }).catch(() => {});
      setSearch('');
      setSelected([]);
      setGroupName('');
    }
  }, [open, im, myId]);

  const filtered = agents.filter(a =>
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.id?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectAgent = async (agent) => {
    if (tab === 0) {
      setLoading(true);
      try {
        const res = await im.createConversation({ type: 'direct', participants: [agent.id] });
        if (res.success) {
          onCreated(res.data);
          onClose();
        }
      } catch (e) {
        console.error('Error creating DM:', e);
      }
      setLoading(false);
    } else {
      setSelected(prev =>
        prev.includes(agent.id)
          ? prev.filter(id => id !== agent.id)
          : [...prev, agent.id]
      );
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selected.length === 0) return;
    setLoading(true);
    try {
      const res = await im.createConversation({
        type: 'group',
        name: groupName.trim(),
        participants: selected
      });
      if (res.success) {
        onCreated(res.data);
        onClose();
      }
    } catch (e) {
      console.error('Error creating group:', e);
    }
    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">Nueva conversación</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#9ca3af' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => { setTab(v); setSelected([]); }}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, fontSize: 13 },
            '& .MuiTabs-indicator': { backgroundColor: accent }
          }}
        >
          <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Mensaje directo" />
          {canCreateGroup && <Tab icon={<GroupAddIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Grupo" />}
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, pt: 1 }}>
        {tab === 1 && (
          <TextField
            fullWidth
            size="small"
            placeholder="Nombre del grupo"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: accent }
              }
            }}
          />
        )}

        {tab === 1 && selected.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {selected.map(id => {
              const agent = agents.find(a => a.id === id);
              return (
                <Chip
                  key={id}
                  label={agent?.name || id}
                  size="small"
                  onDelete={() => setSelected(prev => prev.filter(x => x !== id))}
                  sx={{
                    background: 'rgba(124,58,237,0.1)',
                    color: accent,
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': { color: 'rgba(124,58,237,0.5)' }
                  }}
                />
              );
            })}
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Buscar agente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': { borderColor: accent }
            }
          }}
        />

        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {filtered.map(agent => (
            <ListItem key={agent.id} disablePadding>
              <ListItemButton
                onClick={() => handleSelectAgent(agent)}
                disabled={loading || (tab === 0 && !canMessageAgents && agent.role === 'Agent')}
                sx={{
                  borderRadius: 1,
                  '&:hover': { background: '#f3f4f6' },
                  ...((tab === 0 && !canMessageAgents && agent.role === 'Agent') && { opacity: 0.45 })
                }}
              >
                {tab === 1 && (
                  <Checkbox
                    checked={selected.includes(agent.id)}
                    sx={{ '&.Mui-checked': { color: accent } }}
                  />
                )}
                <ListItemAvatar>
                  <Avatar
                    src={agent.avatar || undefined}
                    sx={{ width: 36, height: 36, bgcolor: 'rgba(124,58,237,0.12)', color: accent, fontSize: 14 }}
                  >
                    {(agent.name || '?')[0]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={agent.name}
                  secondary={agent.role}
                  primaryTypographyProps={{ sx: { fontSize: 14 } }}
                  secondaryTypographyProps={{ sx: { color: '#9ca3af', fontSize: 12 } }}
                />
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: agent.status !== 'OFFLINE' ? '#4caf50' : '#d1d5db'
                }} />
              </ListItemButton>
            </ListItem>
          ))}
          {filtered.length === 0 && (
            <Typography variant="body2" sx={{ color: '#9ca3af', textAlign: 'center', py: 3 }}>
              No se encontraron agentes
            </Typography>
          )}
        </List>
      </DialogContent>

      {tab === 1 && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ color: '#6b7280' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={loading || !groupName.trim() || selected.length === 0}
            sx={{
              background: accent,
              '&:hover': { background: '#6D28D9' }
            }}
          >
            Crear grupo ({selected.length})
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NewConversationDialog;
